import { Component, computed, inject, OnInit, Signal } from '@angular/core';
import { Button } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { InputText } from 'primeng/inputtext';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ExerciseTemplateDto } from '@therapist/shared/models/exercise/exercise-template.dto';
import { Select } from 'primeng/select';
import { TherapistClientDto } from '@therapist/shared/models/client/therapist-client.dto';
import { NgClass } from '@angular/common';
import { TherapistClientStateService } from '@therapist/shared/services/state/therapist-client-state.service';
import { ExerciseStateService } from '@therapist/shared/services/state/exercise-state.service';
import { TherapyPlanTypeEnum } from '@therapist/shared/models/therapy-plan/therapy-plan-type.enum';
import { ExerciseTemplateEditListWrapperComponent } from '../exercise-template-edit-list-wrapper/exercise-template-edit-list-wrapper.component';
import { TherapyPlanStatusEnum, TherapyPlanStatusTranslations } from '@therapist/shared/models/therapy-plan/therapy-plan-status.enum';
import { TherapyPlanAdministrationApiService } from '@therapist/shared/services/api/therapy-plan-administration-api.service';
import { TherapyPlanStateService } from '@therapist/shared/services/state/therapy-plan-state.service';
import { Router } from '@angular/router';
import { TherapyPlanExerciseDto } from '@therapist/shared/models/exercise/therapy-plan-exercise.dto';
import { TherapyPlanDto } from '@therapist/shared/models/therapy-plan/therapy-plan.dto';
import { TherapyPlanCreateDto } from '@therapist/shared/models/therapy-plan/therapy-plan-create.dto';
import { TherapyPlanUpdateDto } from '@therapist/shared/models/therapy-plan/therapy-plan-update.dto';
import { Checkbox } from 'primeng/checkbox';
import { TherapyPlanListItemDto } from '@therapist/shared/models/therapy-plan/therapy-plan-list-item.dto';
import { InputNumber } from 'primeng/inputnumber';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { JointStateService } from '@therapist/shared/services/state/joint-state.service';

@Component({
  selector: 'sana-tp-add-therapy-plan-data',
  imports: [
    Button,
    DatePicker,
    InputText,
    ReactiveFormsModule,
    Select,
    NgClass,
    ExerciseTemplateEditListWrapperComponent,
    Checkbox,
    InputNumber,
    ConfirmDialog
  ],
  templateUrl: './add-therapy-plan-data.component.html',
  styleUrl: './add-therapy-plan-data.component.scss',
  providers: [ConfirmationService]
})
export class AddTherapyPlanDataComponent implements OnInit {

  readonly #fb = inject(FormBuilder);
  readonly #clientStateService = inject(TherapistClientStateService);
  readonly #jointStateService = inject(JointStateService);
  readonly #exerciseStateService = inject(ExerciseStateService);
  readonly #therapyPlanStateService = inject(TherapyPlanStateService);
  readonly #therapyPlanApiService = inject(TherapyPlanAdministrationApiService);
  readonly #router = inject(Router);
  readonly #confirmationService = inject(ConfirmationService);

  selectedClient: Signal<TherapistClientDto | null> = this.#clientStateService.getSelectedClient();

  selectedExerciseList: Signal<ExerciseTemplateDto[]> = this.#exerciseStateService.getSelectedExercises();

  selectedTherapyPlan: Signal<TherapyPlanDto | null> = this.#therapyPlanStateService.getSelectedTherapyPlan();
  cachedTherapyPlanToAdd: Signal<Partial<TherapyPlanCreateDto> | null> = this.#therapyPlanStateService.getCachedTherapyPlanToAdd();

  hasPreviousTherapyPlans: Signal<boolean> = computed(() => this.#therapyPlanStateService.getClientTherapyPlans().length > 0);
  isPreviousTherapyPlanApplied = this.#therapyPlanStateService.getIsPreviousTherapyPlanApplied();

  startDateMinDate = new Date();

  therapyPlanForm = this.#fb.group({
    title: new FormControl<string | null>(null, [
      Validators.required,
      Validators.maxLength(255)
    ]),
    startDate: new FormControl<Date | null>(new Date(), [
      Validators.required
    ]),
    durationInWeeks: new FormControl<number | null>(null, [
      Validators.required
    ]),
    status: new FormControl<TherapyPlanStatusEnum | null>(TherapyPlanStatusEnum.Started, [
      Validators.required
    ]),
    closePreviousTherapyPlan: new FormControl<boolean | null>(false),
    expectedAmountOfTrainingsPerWeek: new FormControl<number | null>(3, [
      Validators.required,
      Validators.min(1),
      Validators.max(Number.MAX_VALUE)
    ])
  });

  isTitleEditMode = false;
  isExerciseWithTrainingSetFormValid = false;
  isSaving = false;

  ngOnInit() {
    const formControls = this.therapyPlanForm.controls;

    if (this.shouldApplyCachedTherapyPlan()) {
      this.applyCachedTherapyPlan();
      return;
    }

    if (this.isPreviousTherapyPlanApplied()) {
      this.applyPreviousTherapyPlan();
      return;
    }

    formControls.title.setValue(`Therapieplan ${this.selectedClient()?.fullName} (${this.cachedTherapyPlanToAdd()!.diagnosisText})`);
  }

  applyCachedTherapyPlan() {
    const cache = this.cachedTherapyPlanToAdd()!;
    const formControls = this.therapyPlanForm.controls;

    if (cache.title) {
      formControls.title.setValue(cache.title);
    }

    if (cache.startDate) {
      formControls.startDate.setValue(cache.startDate);
    }

    if (cache.durationInWeeks) {
      formControls.durationInWeeks.setValue(cache.durationInWeeks);
    }

    if (cache.expectedAmountOfTrainingsPerWeek) {
      formControls.expectedAmountOfTrainingsPerWeek.setValue(cache.expectedAmountOfTrainingsPerWeek);
    }

    if (cache.status) {
      formControls.status.setValue(cache.status);
    }
  }

  shouldApplyCachedTherapyPlan(): boolean {
    const cache = this.cachedTherapyPlanToAdd()!;
    if (cache === null) {
      return false;
    }

    return !!cache.title || !!cache.startDate || !!cache.durationInWeeks || !!cache.expectedAmountOfTrainingsPerWeek || !!cache.status;
  }

  applyPreviousTherapyPlan() {
    const previousPlan = this.#therapyPlanStateService.getPreviousTherapyPlan()()!;
    const formControls = this.therapyPlanForm.controls;

    formControls.title.setValue(previousPlan.title);
    formControls.durationInWeeks.setValue(previousPlan.durationInWeeks);
    formControls.expectedAmountOfTrainingsPerWeek.setValue(previousPlan.expectedAmountOfTrainingsPerWeek);
  }

  removeExercise(id: string): void {
    this.#exerciseStateService.removeSelectedExercise(id);
    this.#therapyPlanStateService.removeTherapyPlanExercise(id);
  }

  updateTherapyPlanExercises(exercise: TherapyPlanExerciseDto[]): void {
    exercise.forEach((exercise: TherapyPlanExerciseDto) => this.#therapyPlanStateService.addOrUpdateTherapyPlanExercise(exercise));
  }

  submit($event: any): void {
    this.therapyPlanForm.markAllAsTouched();

    if (this.therapyPlanForm.invalid) {
      window.scroll({ top: 0, behavior: 'smooth' });
      return;
    }

    if (this.isSaving || !this.isExerciseWithTrainingSetFormValid) {
      return;
    }

    this.isSaving = true;
    const newPlan = this.createTherapyPlanCreateModel();

    if (this.selectedTherapyPlan()) {
      this.updateTherapyPlan(newPlan);
    } else {

      const isFirstTherapyPlan = this.#therapyPlanStateService.getClientTherapyPlans()().length === 0;

      if (isFirstTherapyPlan) {
        this.handleSavingOfFirstTherapyPlan(newPlan, $event);
      } else {
        this.saveNewTherapyPlan(newPlan);
      }
    }
  }

  private handleSavingOfFirstTherapyPlan(newPlan: TherapyPlanCreateDto, $event: any): void {
    this.#confirmationService.confirm({
      target: $event.target as EventTarget,
      message: `Sie erstellen den ersten Therapieplan für diesen Patienten. Gleichzeitig senden wir automatisch eine Einladung zur Sanactivo-App an ${this.selectedClient()!.email}. Möchten Sie fortfahren?`,
      header: 'Einladung an Patient',
      closable: false,
      closeOnEscape: false,
      rejectButtonProps: {
        label: 'Abbrechen',
        severity: 'secondary'
      },
      acceptButtonProps: {
        label: 'Speichern und Einladung senden'
      },
      accept: () => this.saveNewTherapyPlan(newPlan),
      reject: () => this.isSaving = false
    });
  }

  private createTherapyPlanCreateModel(): TherapyPlanCreateDto {
    const formValues = this.therapyPlanForm.getRawValue();

    return {
      title: formValues.title!,
      startDate: formValues.startDate!,
      durationInWeeks: formValues.durationInWeeks!,
      expectedAmountOfTrainingsPerWeek: formValues.expectedAmountOfTrainingsPerWeek!,
      status: formValues.status!,

      selectedJointIds: this.cachedTherapyPlanToAdd()!.selectedJointIds!,
      diagnosisText: this.cachedTherapyPlanToAdd()!.diagnosisText!,
      therapyPlanType: this.cachedTherapyPlanToAdd()!.therapyPlanType!,
      surgeryMethodText: this.cachedTherapyPlanToAdd()!.therapyPlanType! === TherapyPlanTypeEnum.Surgical ? this.cachedTherapyPlanToAdd()!.surgeryMethodText! : null,

      selectedExercises: this.#therapyPlanStateService.getTherapyPlanExercises()(),
      clientId: this.selectedClient()?.id!
    };
  }

  private saveNewTherapyPlan(therapyPlan: TherapyPlanCreateDto): void {
    this.#therapyPlanApiService
      .save(therapyPlan)
      .subscribe({
        next: (therapyPlan: TherapyPlanDto) => this.handleSubmitSuccess(therapyPlan),
        complete: () => this.isSaving = false
      });
  }

  private updateTherapyPlan(newPlan: TherapyPlanCreateDto): void {
    const updatePlan: TherapyPlanUpdateDto = {
      ...newPlan,
      id: this.selectedTherapyPlan()!.id!
      // closePreviousTherapyPlan: this.therapyPlanForm.controls.closePreviousTherapyPlan!.value!
    };

    this.#therapyPlanApiService
      .update(updatePlan)
      .subscribe({
        next: (therapyPlan: TherapyPlanDto) => this.handleSubmitSuccess(therapyPlan),
        complete: () => this.isSaving = false
      });
  }

  private handleSubmitSuccess(therapyPlan: TherapyPlanDto): void {
    this.#jointStateService.removeAllSelectedJoints();
    this.#exerciseStateService.removeAll();
    this.#therapyPlanStateService.removeAllTherapyPlanExercises();
    this.#therapyPlanStateService.setCachedTherapyPlanToAdd(null);
    this.#therapyPlanStateService.addTherapyPlanToClientTherapyPlans(this.fromTherapyPlanToTherapyPlanListItem(therapyPlan));
    this.#therapyPlanStateService.setSelectedTherapyPlan(therapyPlan, true);
    this.#router.navigate([`/client/${this.selectedClient()?.id!}`]);
  }

  private fromTherapyPlanToTherapyPlanListItem(therapyPlan: TherapyPlanDto): TherapyPlanListItemDto {
    return {
      id: therapyPlan.id,
      title: therapyPlan.title,
      type: therapyPlan.therapyPlanType,
      status: therapyPlan.status,
      startDate: therapyPlan.startDate,
      endDate: therapyPlan.endDate,
      descriptionShort: therapyPlan.description ? therapyPlan.description : '',
      clientId: therapyPlan.clientId
    };
  }

  getDurationTranslation(durationInWeeks: number): string {
    if (durationInWeeks === 1) {
      return `${durationInWeeks} Woche`;
    }

    return `${durationInWeeks} Wochen`;
  }

  saveToCache(): void {
    const cache = this.cachedTherapyPlanToAdd()!;
    const formControls = this.therapyPlanForm.getRawValue();

    this.#therapyPlanStateService.setCachedTherapyPlanToAdd(
      {
        ...cache,
        title: formControls.title!,
        startDate: formControls.startDate!,
        durationInWeeks: formControls.durationInWeeks!,
        status: formControls.status!
      }
    );
  }

  protected readonly TherapyPlanStatusTranslations = TherapyPlanStatusTranslations;
  protected readonly TherapyPlanStatusEnum = TherapyPlanStatusEnum;

}
